import styled from "styled-components";
import CustomIcon from "../image/CustomIcon";
import { useEffect, useRef, useState } from "react";
import { useClickOutside } from "../../hooks/useClickOutside";
import { Container, Padding, Row, SizedBox } from "../CommonBaseComponents";
import CustomImage from "../image/CustomImage";
import { C4Text } from "../text/CustomText";
import { formatCurrentTime } from "../PageTitle";
import { useDevice } from "../../hooks/useDevice";
import { UserDropdownMenu } from "./WebContentWrapper";
import LanguageDropdownMenu, { Languages } from "../dropdown/LanguageDropdownMenu";
import LocalStorageKey from "../../enums/LocalStorageKey";
import { Theme } from "../../Theme";
import GnbSidebar from "../sidebar/GnbSidebar";

const BackgroundWrapper = styled.div`
    height: 100%;
    width: 100vw;
    display: flex;
    flex-direction: column;
    box-sizing: border-box;
`;

const ContentWrapper = styled.div`
    width: 100%;
    background-color: ${Theme.color.white};
    border-radius: 16px;
    overflow: hidden;
`;

const IconWrapper = styled.div`
    width: 36px;
    height: 36px;
    border-radius: 100%;
    background-color: ${Theme.color.neutral200};
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
`;

const MenuContainer = styled.div`
    position: relative;
    display: flex;
    align-items: center;
`;

export function MobileContentWrapper({ children }) {
    // gnbMenu
    const [isGnbMenuOpen, setIsGnbMenuOpen] = useState(false);

    const toggleGnbMenu = () => {
        setIsGnbMenuOpen((prev) => !prev);
    };

    // userMenu
    const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);

    const toggleUserMenu = () => {
        setIsUserMenuOpen((prev) => !prev);
    };

    const userMenuRef = useRef(null);
    useClickOutside(userMenuRef, () => {
        if (isUserMenuOpen) {
            setIsUserMenuOpen(false);
        }
    });

    // languageMenu
    const [isLangMenuOpen, setIsLangMenuOpen] = useState(false);
    const [currentTime, setCurrentTime] = useState(new Date());
    const [currentLanguage] = useState(() => localStorage.getItem(LocalStorageKey.language) || "en");
    const currentDevice = useDevice();
    const languageButtonRef = useRef(null); // 언어 버튼 ref 추가
    let countrySection;

    useEffect(() => {
        const timerId = setInterval(() => {
            setCurrentTime(new Date());
        }, 1000);

        return () => {
            clearInterval(timerId);
        };
    }, []);

    const toggleLanguageMenu = () => {
        setIsLangMenuOpen((prev) => !prev);
    };

    const languageMenuRef = useRef(null);
    useClickOutside(languageMenuRef, (event) => {
        if (isLangMenuOpen) {
            const dropdown = document.querySelector('[data-is-dropdown="true"]');
            if (dropdown && dropdown.contains(event.target)) {
                return;
            }
            setIsLangMenuOpen(false);
        }
    });

    const handleLanguageChange = (newLang) => {
        localStorage.setItem(LocalStorageKey.language, newLang);
        window.location.reload();
    };

    if (currentDevice === "mobile") {
        countrySection = (
            <MenuContainer ref={languageMenuRef}>
                <Container
                    ref={languageButtonRef} // ref 연결
                    padding={"6px"}
                    borderRadius={"100%"}
                    border={`1px solid ${Theme.color.neutral200}`}
                    cursor={"pointer"}
                    onClick={toggleLanguageMenu}
                    hoverBackgroundColor={Theme.color.neutral100}
                >
                    <CustomImage
                        imageName={
                            Languages.filter((v) => {
                                return v.value === currentLanguage;
                            })[0]?.image
                        }
                        size={"24px"}
                        cursor={"pointer"}
                        borderRadius={"100%"}
                    />
                </Container>
                {isLangMenuOpen && (
                    <LanguageDropdownMenu
                        initValue={currentLanguage}
                        setValue={handleLanguageChange}
                        values={Languages}
                        onClose={toggleLanguageMenu}
                        anchorEl={languageButtonRef.current} // anchorEl 전달
                    />
                )}
            </MenuContainer>
        );
    } else {
        countrySection = (
            <Row>
                <CustomIcon iconName={"alarm"} size={"13.5px"} />
                <SizedBox width={"6px"} />
                <C4Text
                    fontColor={Theme.color.gray500}
                    style={{
                        fontVariantNumeric: "tabular-nums",
                        textAlign: "left",
                        width: 108,
                        whiteSpace: "nowrap",
                    }}
                >
                    {formatCurrentTime(currentTime)}
                </C4Text>
                <SizedBox width={"35px"} />
                <MenuContainer ref={languageMenuRef}>
                    <Container
                        ref={languageButtonRef} // ref 연결
                        padding={"6px"}
                        borderRadius={"100%"}
                        border={`1px solid ${Theme.color.neutral200}`}
                        cursor={"pointer"}
                        onClick={toggleLanguageMenu}
                        hoverBackgroundColor={Theme.color.neutral100}
                    >
                        <CustomImage
                            imageName={
                                Languages.filter((v) => {
                                    return v.value === currentLanguage;
                                })[0]?.image
                            }
                            size={"24px"}
                            cursor={"pointer"}
                            borderRadius={"100%"}
                        />
                    </Container>
                    {isLangMenuOpen && (
                        <LanguageDropdownMenu
                            initValue={currentLanguage}
                            setValue={handleLanguageChange}
                            values={Languages}
                            onClose={toggleLanguageMenu}
                            anchorEl={languageButtonRef.current} // anchorEl 전달
                        />
                    )}
                </MenuContainer>
            </Row>
        );
    }

    return (
        <BackgroundWrapper>
            <Row justifyContent={"space-between"} $borderTop $borderBottom padding={"12px 0 15px 0"}>
                <Row justifyContent={"center"}>
                    <SizedBox width={"20px"} />
                    <CustomIcon iconName={"logo"} size={"42px"} />
                    <SizedBox width={"10px"} />
                    <MenuContainer>
                        <Padding padding={"4px"}>
                            <CustomIcon
                                iconName={"mobile_menu_v2"}
                                size={"24px"}
                                cursor={"pointer"}
                                onClick={toggleGnbMenu}
                            />
                        </Padding>
                        <GnbSidebar isOpen={isGnbMenuOpen} onClose={toggleGnbMenu} />
                    </MenuContainer>
                </Row>
                <Row>
                    {countrySection}
                    <SizedBox width={"10px"} />
                    <MenuContainer ref={userMenuRef}>
                        <IconWrapper onClick={toggleUserMenu}>
                            <CustomIcon iconName={"user"} cursor={"pointer"} />
                            {isUserMenuOpen && <UserDropdownMenu topPosition={"100%"} leftPosition={"-500%"} />}
                        </IconWrapper>
                    </MenuContainer>
                    <SizedBox width={"20px"} />
                </Row>
            </Row>
            <ContentWrapper>{children}</ContentWrapper>
        </BackgroundWrapper>
        // </>
    );
}
